import React, { useState, useCallback } from 'react';
import { cn } from '../../../utils/cn';
import {
  avatarVariants,
  avatarImageStyles,
  avatarInitialsStyles,
  statusIndicatorVariants,
  ringVariants,
} from './Avatar.styles';
import { AvatarProps } from './Avatar.types';

/**
 * Extracts initials from a name string
 * @param name - Full name to extract initials from
 * @returns Up to 2 characters representing the initials
 */
function getInitials(name: string): string {
  if (!name) return '';

  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return '';

  const firstWord = words[0];
  const lastWord = words[words.length - 1];

  if (words.length === 1 && firstWord) {
    // Single word: take first two characters
    return firstWord.slice(0, 2);
  }

  // Multiple words: take first character of first and last word
  const firstInitial = firstWord?.[0] ?? '';
  const lastInitial = lastWord?.[0] ?? '';

  return `${firstInitial}${lastInitial}`;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      src,
      alt,
      name,
      size,
      shape,
      status,
      ring = false,
      ringColor = 'primary',
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = useState(false);

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    const handleImageLoad = useCallback(() => {
      setImageError(false);
    }, []);

    const showImage = src && !imageError;
    const initials = name ? getInitials(name) : '';

    return (
      <div
        ref={ref}
        className={cn(avatarVariants({ size, shape }), ring && ringVariants[ringColor], className)}
        role="img"
        aria-label={alt || name || 'Avatar'}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className={avatarImageStyles}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <span className={avatarInitialsStyles} aria-hidden="true">
            {initials}
          </span>
        )}

        {status && (
          <span
            className={cn(statusIndicatorVariants({ size, status }))}
            aria-label={`Status: ${status}`}
            role="status"
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
